import { useState, useEffect } from 'react'
import { Menu, LogOut, User as UserIcon, Bell, BellOff, BellRing } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import toast from 'react-hot-toast'
import { Button } from '@/components/ui/button'
import {
  getNotificationPermission,
  requestNotificationPermission,
  notificationsSupported,
} from '@/lib/notifications'
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { useAuth } from '@/contexts/AuthContext'

const initials = (name = '') =>
  name
    .split(' ')
    .map((n) => n[0])
    .slice(0, 2)
    .join('')
    .toUpperCase() || 'U'

export default function Header({ onToggleSidebar }) {
  const { user, logout } = useAuth()
  const navigate = useNavigate()
  const [notifPerm, setNotifPerm] = useState(getNotificationPermission())

  // Pide permiso automáticamente al entrar (tras login), sin esperar al botón.
  // Algunos navegadores (Firefox/Safari) lo ignoran sin un clic previo; para
  // esos casos queda el botón 🔔 como respaldo.
  useEffect(() => {
    if (notificationsSupported() && Notification.permission === 'default') {
      requestNotificationPermission().then(setNotifPerm)
    }
  }, [])

  const handleLogout = () => {
    logout()
    navigate('/login', { replace: true })
  }

  const handleEnableNotifications = async () => {
    if (!notificationsSupported()) {
      toast.error('Tu navegador no soporta notificaciones')
      return
    }
    if (Notification.permission === 'denied') {
      toast.error('Notificaciones bloqueadas. Actívalas en los ajustes del navegador (icono del candado).')
      return
    }
    const result = await requestNotificationPermission()
    setNotifPerm(result)
    if (result === 'granted') toast.success('Notificaciones activadas')
    else if (result === 'denied') toast.error('Has rechazado las notificaciones')
  }

  return (
    <header className="flex h-16 items-center justify-between gap-4 border-b bg-background px-4 md:px-6">
      <div className="flex items-center gap-2">
        <Button
          variant="ghost"
          size="icon"
          className="md:hidden"
          onClick={onToggleSidebar}
          aria-label="Abrir menú"
        >
          <Menu className="h-5 w-5" />
        </Button>
      </div>

      <div className="flex items-center gap-1">
        {notificationsSupported() && (
          <Button
            variant="ghost"
            size="icon"
            onClick={handleEnableNotifications}
            aria-label="Notificaciones"
            title={
              notifPerm === 'granted'
                ? 'Notificaciones activadas'
                : notifPerm === 'denied'
                  ? 'Notificaciones bloqueadas — actívalas en el navegador'
                  : 'Activar notificaciones'
            }
          >
            {notifPerm === 'granted' ? (
              <BellRing className="h-5 w-5 text-emerald-600" />
            ) : notifPerm === 'denied' ? (
              <BellOff className="h-5 w-5 text-muted-foreground" />
            ) : (
              <Bell className="h-5 w-5" />
            )}
          </Button>
        )}

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" className="gap-3 px-2 h-auto py-1.5">
            <Avatar className="h-8 w-8">
              {user?.image && <AvatarImage src={user.image} alt={user?.name || 'Perfil'} />}
              <AvatarFallback className="text-xs">{initials(user?.name)}</AvatarFallback>
            </Avatar>
            <div className="hidden sm:flex flex-col items-start">
              <span className="text-sm font-medium leading-none">{user?.name}</span>
              <span className="text-xs text-muted-foreground capitalize mt-0.5">
                {typeof user?.role === 'string' ? user.role : user?.role?.name || '—'}
              </span>
            </div>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          <DropdownMenuLabel>
            <div className="flex flex-col">
              <span className="font-medium">{user?.name}</span>
              <span className="text-xs text-muted-foreground capitalize">
                {typeof user?.role === 'string' ? user.role : user?.role?.name || '—'}
              </span>
              <span className="text-xs text-muted-foreground">{user?.email}</span>
            </div>
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={() => navigate('/profile')}>
            <UserIcon className="h-4 w-4" />
            Mi perfil
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={handleLogout} className="text-destructive focus:text-destructive">
            <LogOut className="h-4 w-4" />
            Cerrar sesión
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      </div>
    </header>
  )
}
